import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useAuth, SignInButton } from '@clerk/react';
import theme from '../../theme';
import PageHeader from '../../components/PageHeader';
import { fetchCourse, markLessonComplete } from '../../lib/lmsApi';
import { MAIN_SITE_URL } from '../../lib/subdomain';
import GarrisonTriangle from '../../components/GarrisonTriangle';
import EconomicPlanner from '../../components/EconomicPlanner';
import Quiz from '../../components/Quiz';
import FredChart from '../../components/FredChart';

// Keyed on Lesson.readings[].kind (ADR-0015). Audio is deliberately distinct
// from video: "watch" a podcast is the kind of wrong wording that makes a
// course look unmaintained.
const LINK_LABEL = { text: 'read free', video: 'watch free', audio: 'listen free' };

// Keep in sync with backend/models/Lesson.js's EMBEDDABLE_TOOLS.
const TOOL_COMPONENTS = {
  'garrison-triangle': GarrisonTriangle,
  'calculation-problem': EconomicPlanner,
  quiz: Quiz,
  fred: FredChart,
};

const CSS = `
  .lesson-readings {
    max-width: 720px;
    margin: 44px 0 8px;
    padding: 26px 30px;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    border-radius: 4px;
    background: rgba(var(--ap-gold-rgb), 0.04);
  }
  .lesson-readings h2 {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${theme.gold};
    margin: 0 0 18px;
  }
  .lesson-readings ol {
    margin: 0;
    padding: 0;
    list-style: none;
    counter-reset: reading;
  }
  .lesson-readings li {
    counter-increment: reading;
    position: relative;
    padding-left: 30px;
    margin-bottom: 20px;
  }
  .lesson-readings li:last-of-type { margin-bottom: 0; }
  .lesson-readings li::before {
    content: counter(reading);
    position: absolute;
    left: 0;
    top: 1px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 12px;
    color: ${theme.gold};
    font-variant-numeric: tabular-nums;
  }
  .lesson-readings li.further { opacity: 0.82; }
  .reading-cite {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    color: ${theme.textPrimary};
    margin: 0 0 5px;
    line-height: 1.5;
  }
  .reading-author { font-weight: 600; }
  .lesson-readings cite { font-style: italic; }
  .reading-locator { color: ${theme.textMuted}; }
  .reading-free {
    font-family: 'Josefin Sans', sans-serif;
    font-size: 11px;
    letter-spacing: 0.05em;
    margin-left: 8px;
    white-space: nowrap;
  }
  .reading-free a { color: ${theme.gold}; text-decoration: none; border-bottom: 1px solid rgba(var(--ap-gold-rgb), 0.4); }
  .reading-free a:hover { border-bottom-color: ${theme.gold}; }
  .reading-tag {
    display: inline-block;
    margin-left: 9px;
    padding: 1px 7px;
    border: 1px solid rgba(var(--ap-text-muted-rgb), 0.4);
    border-radius: 2px;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${theme.textMuted};
    vertical-align: middle;
  }
  .reading-note {
    font-family: 'EB Garamond', serif;
    font-size: 15.5px;
    color: ${theme.textMuted};
    line-height: 1.65;
    margin: 0;
  }
  .reading-foot {
    font-family: 'EB Garamond', serif;
    font-size: 14px;
    color: ${theme.textMuted};
    margin: 22px 0 0;
    padding-top: 16px;
    border-top: 1px solid rgba(var(--ap-text-muted-rgb), 0.2);
  }
  .lesson-body {
    font-family: 'EB Garamond', serif;
    font-size: 17.5px;
    color: ${theme.textPrimary};
    line-height: 1.8;
    max-width: 720px;
  }
  .lesson-body p {
    margin: 0 0 1.3em;
  }
  .lesson-body strong {
    color: ${theme.gold};
  }
  .lesson-embed {
    margin: 40px 0;
    border: 1px solid rgba(var(--ap-gold-rgb), 0.2);
    border-radius: 4px;
    overflow: hidden;
  }
  .lesson-locked {
    margin: 24px 0 32px;
    padding: 24px;
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.25);
    border-radius: 4px;
  }
  .lesson-locked p {
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    color: ${theme.textMuted};
    margin: 0 0 16px;
  }
  .lesson-tool-link {
    display: inline-block;
    margin: 32px 0;
    padding: 12px 22px;
    background: ${theme.bgCard};
    border: 1px solid rgba(var(--ap-gold-rgb), 0.3);
    border-radius: 4px;
    color: ${theme.gold};
    text-decoration: none;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }
  .lesson-nav {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 48px;
    padding-top: 24px;
    border-top: 1px solid rgba(var(--ap-gold-rgb), 0.2);
    gap: 16px;
  }
  .lesson-nav a {
    color: ${theme.gold};
    text-decoration: none;
    font-family: 'Josefin Sans', sans-serif;
    font-size: 13px;
    letter-spacing: 0.05em;
  }
`;

// "3 min - 2 readings". The minutes describe the prose only, which is short by
// design; the readings are where the programme actually is (ADR-0007), so the
// count carries the weight the estimate cannot. Omitted when a lesson assigns
// nothing rather than printing "0 readings".
function lessonMeta(lesson) {
  const mins = `${lesson.estimatedMinutes} min`;
  const n = lesson.readings?.length || 0;
  return n ? `${mins} · ${n} reading${n === 1 ? '' : 's'}` : mins;
}

function LessonPage() {
  const { courseSlug, lessonSlug } = useParams();
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const [course, setCourse] = useState(null);
  const [error, setError] = useState(false);
  const [completing, setCompleting] = useState(false);
  // Set only by clicking the button. The stored answer comes from the fetched
  // progress below; this covers the gap between clicking and a refetch.
  const [justCompleted, setJustCompleted] = useState(false);

  // Waits for Clerk, then sends the token: the response depends on having a
  // session (ADR-0012 withholds lesson prose from anonymous callers) and it
  // carries the caller's own completed-lesson ids, which is how this page
  // knows what has already been done. Before this it never read progress back,
  // so a lesson finished last week still showed 'Mark Complete'.
  useEffect(() => {
    window.scrollTo(0, 0);
    setCourse(null);
    setJustCompleted(false);
    if (!isLoaded) return;
    fetchCourse(courseSlug, isSignedIn ? getToken : null)
      .then(setCourse)
      .catch(() => setError(true));
  }, [courseSlug, lessonSlug, isLoaded, isSignedIn, getToken]);

  if (error) {
    return <div className="page-container"><p style={{ color: theme.error }}>Couldn't load this lesson.</p></div>;
  }
  if (!course) {
    return <div className="page-container"><p style={{ color: theme.textMuted }}>Loading...</p></div>;
  }

  const lessons = course.lessons;
  const index = lessons.findIndex((l) => l.slug === lessonSlug);
  const lesson = lessons[index];

  if (!lesson) {
    return <div className="page-container"><p style={{ color: theme.error }}>Lesson not found.</p></div>;
  }

  document.title = `${lesson.title} - Austrian Process`;

  const completed = justCompleted
    || (course.progress?.completedLessonIds || []).includes(lesson._id);

  const ToolComponent = lesson.embeddedTool ? TOOL_COMPONENTS[lesson.embeddedTool] : null;
  const prev = index > 0 ? lessons[index - 1] : null;
  const next = index < lessons.length - 1 ? lessons[index + 1] : null;

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await markLessonComplete(getToken, course.slug, lesson.slug);
      setJustCompleted(true);
    } catch {
      // leave it unticked; user can retry
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className="page-container">
      <style>{CSS}</style>
      <div style={{ fontFamily: "'Josefin Sans', sans-serif", fontSize: '12px', color: theme.textMuted, marginBottom: '16px' }}>
        <Link to={`/${course.slug}`} style={{ color: theme.textMuted, textDecoration: 'none' }}>{course.title}</Link>
        {' / '}Lesson {lesson.order} of {lessons.length}
      </div>

      <PageHeader title={lesson.title} description={lessonMeta(lesson)} />

      {course.lessonBodiesIncluded ? (
        <div className="lesson-body">
          <ReactMarkdown>{lesson.contentBody}</ReactMarkdown>
        </div>
      ) : (
        // Not an error state: the reading list below is the substance of the
        // programme (ADR-0007) and stays public, so a signed-out visitor still
        // sees what this lesson asks them to read and can go and read it.
        <div className="lesson-locked">
          <p>Sign in to read this lesson. The assigned readings below are open to everyone.</p>
          <SignInButton mode="modal">
            <button type="button" className="btn-primary">Sign in to read</button>
          </SignInButton>
        </div>
      )}

      {ToolComponent && (
        <div className="lesson-embed">
          <ToolComponent />
        </div>
      )}
      {lesson.toolLink && (
        // Points at a page on the main site (e.g. /ask-austrian, /fred) --
        // an absolute cross-origin link, not a React Router <Link>, since
        // this page only ever renders on the learn.* subdomain now.
        <a href={`${MAIN_SITE_URL}${lesson.toolLink.path}`} className="lesson-tool-link">
          {lesson.toolLink.label} &rarr;
        </a>
      )}

      {/* The lesson text is a few hundred words by design; these are where the
          actual programme is (ADR-0007). Rendered after the tool so the reading
          is the last thing on the page -- it's what you leave to go and do. */}
      {lesson.readings?.length > 0 && (
        <section className="lesson-readings">
          <h2>Read this</h2>
          <ol>
            {lesson.readings.map((r, i) => (
              <li key={i} className={r.essential === false ? 'further' : ''}>
                <p className="reading-cite">
                  <span className="reading-author">{r.author}</span>
                  {', '}
                  <cite>{r.work}</cite>
                  {r.locator ? <span className="reading-locator">{` - ${r.locator}`}</span> : null}
                  {r.url && (
                    <span className="reading-free">
                      <a href={r.url} target="_blank" rel="noopener noreferrer">
                        {LINK_LABEL[r.kind] || LINK_LABEL.text} &rarr;
                      </a>
                    </span>
                  )}
                  {r.kind === 'video' || r.kind === 'audio' ? <span className="reading-tag">lecture</span> : null}
                  {r.essential === false ? <span className="reading-tag">further</span> : null}
                </p>
                {r.note && <p className="reading-note">{r.note}</p>}
              </li>
            ))}
          </ol>
          <p className="reading-foot">
            Most of these are freely available at mises.org and econlib.org. Editions vary, so
            chapters are cited rather than page numbers.
          </p>
        </section>
      )}

      <div>
        {isLoaded && (isSignedIn ? (
          completed ? (
            <button type="button" className="btn-success" disabled>
              &#10003; Completed
            </button>
          ) : (
            <button type="button" className="btn-primary" onClick={handleComplete} disabled={completing}>
              {completing ? 'Saving...' : 'Mark Complete'}
            </button>
          )
        ) : (
          <SignInButton mode="modal">
            <button type="button" className="btn-secondary">Sign in to track progress</button>
          </SignInButton>
        ))}
      </div>

      <div className="lesson-nav">
        {prev ? <Link to={`/${course.slug}/${prev.slug}`}>&larr; {prev.title}</Link> : <span />}
        {next ? <Link to={`/${course.slug}/${next.slug}`}>{next.title} &rarr;</Link> : <Link to={`/${course.slug}`}>Back to course &rarr;</Link>}
      </div>
    </div>
  );
}

export default LessonPage;
